import React, { useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { PermissionGuard } from '@/components/auth/PermissionGuard';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import { Paintbrush, Eye, EyeOff, Palette as ColorPaletteIcon, ChevronDown } from 'lucide-react';
import { HighlightStyle } from './EditorInteractable';
import {
  selectHighlightStyle,
  selectHighlightColor,
  selectHighlightsEnabled,
  setHighlightStyle,
  setHighlightColor,
  toggleHighlights,
  saveEditorSettings,
  initializeFromProfile,
  HighlightColor,
  selectEditorSettingsSaving
} from '@/store/slices/editorSettingsSlice';
import { selectUserProfile } from '@/store/slices/authSlice';

interface EditorControlsProps {
  // className prop is no longer used as styling is self-contained for popover
}

export const EditorControls: React.FC<EditorControlsProps> = () => {
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector(selectUserProfile);

  const highlightStyle = useAppSelector(selectHighlightStyle);
  const highlightColor = useAppSelector(selectHighlightColor);
  const highlightsEnabled = useAppSelector(selectHighlightsEnabled);
  const isSaving = useAppSelector(selectEditorSettingsSaving);

  useEffect(() => {
    if (userProfile?.editorSettings) {
      const settingsFromProfile = userProfile.editorSettings;
      // Ensure a complete settings object is passed to initializeFromProfile,
      // falling back to sensible defaults if properties are missing from the profile.
      // These fallbacks should ideally align with the initial state of editorSettingsSlice.
      dispatch(initializeFromProfile({
        highlightStyle: settingsFromProfile.highlightStyle ?? 'border',
        highlightColor: settingsFromProfile.highlightColor ?? 'blue',
        highlightsEnabled: settingsFromProfile.highlightsEnabled ?? true,
      }));
    }
    // The dependency array ensures this effect runs if the userProfile or its editorSettings change.
    // It also correctly references `dispatch`.
  }, [dispatch, userProfile?.editorSettings]);

  // Simplified save function - just dispatch saveEditorSettings
  // Redux extraReducers will update authSlice.profile.editorSettings automatically
  const saveSettings = useCallback(async () => {
    await dispatch(saveEditorSettings());
  }, [dispatch]);

  const getHighlightStyleLabel = (style: HighlightStyle): string => {
    switch (style) {
      case 'background': return 'Background';
      case 'border': return 'Border';
      case 'none': return 'None';
      default: return 'Unknown';
    }
  };

  const getColorLabel = (color: HighlightColor): string => {
    return color.charAt(0).toUpperCase() + color.slice(1);
  };

  const handleHighlightStyleChange = (style: HighlightStyle) => {
    dispatch(setHighlightStyle(style));
    saveSettings();
  };

  const handleHighlightColorChange = (color: HighlightColor) => {
    dispatch(setHighlightColor(color));
    saveSettings();
  };

  const handleToggleHighlights = () => {
    dispatch(toggleHighlights());
    saveSettings();
  };

  const highlightStyleOptions: { value: HighlightStyle; label: string }[] = [
    { value: 'background', label: getHighlightStyleLabel('background') },
    { value: 'border', label: getHighlightStyleLabel('border') },
  ];

  const highlightColorOptions: { value: HighlightColor; label: string; indicatorClass: string }[] = [
    { value: 'blue', label: getColorLabel('blue'), indicatorClass: 'bg-blue-400' },
    { value: 'green', label: getColorLabel('green'), indicatorClass: 'bg-green-400' },
    { value: 'purple', label: getColorLabel('purple'), indicatorClass: 'bg-purple-400' },
    { value: 'orange', label: getColorLabel('orange'), indicatorClass: 'bg-orange-400' },
    { value: 'red', label: getColorLabel('red'), indicatorClass: 'bg-red-400' },
  ];

  return (
    <PermissionGuard requiredPermission="editor">
      <div className="p-4 space-y-4 w-64">
        
        <div>
          <h4 className="mb-2 text-sm font-medium text-muted-foreground">Highlight Visibility</h4>
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full justify-start"
            onClick={handleToggleHighlights}
            disabled={isSaving}
          >
            {highlightsEnabled ? (
              <>
                <Eye className="h-4 w-4 mr-2" />
                <span>Highlights On</span>
              </>
            ) : (
              <>
                <EyeOff className="h-4 w-4 mr-2" />
                <span>Highlights Off</span>
              </>
            )}
          </Button>
        </div>

        <DropdownMenuSeparator />

        <div>
          <h4 className="mb-2 text-sm font-medium text-muted-foreground">Highlight Style</h4>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full justify-between"
                disabled={isSaving}
              >
                <div className="flex items-center">
                  <Paintbrush className="h-4 w-4 mr-2" />
                  <span>{getHighlightStyleLabel(highlightStyle)}</span>
                </div>
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="start" 
              className="w-56" 
              onPointerDown={(e) => e.stopPropagation()}
            >
              <DropdownMenuRadioGroup value={highlightStyle} onValueChange={(value) => handleHighlightStyleChange(value as HighlightStyle)}>
                {highlightStyleOptions.map((option) => (
                  <DropdownMenuRadioItem key={option.value} value={option.value}>
                    {option.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <DropdownMenuSeparator />

        <div>
          <h4 className="mb-2 text-sm font-medium text-muted-foreground">Highlight Color</h4>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full justify-between"
                disabled={isSaving}
              >
                <div className="flex items-center">
                  <ColorPaletteIcon className="h-4 w-4 mr-2" />
                  <span>{getColorLabel(highlightColor)}</span>
                </div>
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="start" 
              className="w-56" 
              onPointerDown={(e) => e.stopPropagation()}
            >
               <DropdownMenuRadioGroup value={highlightColor} onValueChange={(value) => handleHighlightColorChange(value as HighlightColor)}>
                {highlightColorOptions.map((option) => (
                  <DropdownMenuRadioItem key={option.value} value={option.value}>
                    <div className="flex items-center">
                      <div className={`h-3 w-3 mr-2 rounded-full ${option.indicatorClass}`}></div>
                      {option.label}
                    </div>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </PermissionGuard>
  );
}; 